/**
 * Configuración → «Conversaciones»: el aviso por mail cuando alguien espera respuesta y las respuestas
 * rápidas de la bandeja (spec de conversaciones §15.6 y §11).
 *
 * Guarda como las otras secciones (`useFormularioConfig`: sólo lo que cambió, validado con la regla del
 * backend), con dos cuidados propios de esta:
 * - La fila nueva nace con su `id` (8 hex). Con `id: ""` el backend generaba uno, lo guardado nunca
 *   coincidía con lo editado y «Guardar cambios» quedaba habilitado para siempre.
 * - El trim de título, texto y mail se hace ANTES de mandar. El backend también lo hace, y un espacio de más
 *   dejaba el formulario con cambios después de guardar. No se hace mientras se escribe: el espacio entre
 *   dos palabras es, por un instante, un espacio al final.
 *
 * El mail no lleva lo que escribió la persona ni su número: sólo cuántas esperan y el link a la bandeja.
 */

import { useEffect, useId, useState } from "react";
import { Mail, MessageSquareText, Plus, Trash2 } from "lucide-react";
import type { RespuestaRapida } from "../../types.conversaciones";
import { useCuentaOficial } from "../../contexto";
import { Tarjeta } from "../../components/Tarjeta";
import { AvisoAmbar } from "../../components/base/comun";
import {
  MAX_RESPUESTAS_RAPIDAS, MAX_TEXTO_RAPIDA, MAX_TITULO_RAPIDA, idRespuestaRapida, limpiarRespuestasRapidas,
} from "../../logic/config";
import { BotoneraGuardar, useFormularioConfig } from "./SeccionEnvio";

export default function SeccionConversaciones() {
  const { cuenta } = useCuentaOficial();
  const f = useFormularioConfig();
  const idMail = useId();
  // Guardar con algo para limpiar: primero se limpia el formulario y, en la vuelta siguiente (ya con lo
  // limpio en `cambios`), se guarda. Llamar a `f.guardar()` en el mismo clic mandaría lo de antes.
  const [guardarLimpio, setGuardarLimpio] = useState(false);

  useEffect(() => {
    if (!guardarLimpio) return;
    setGuardarLimpio(false);
    void f.guardar();
    // Sólo cuando se pide: `f` es un objeto nuevo en cada render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardarLimpio]);

  const e = f.editado;
  if (!e) return null;

  const mail = e.mailAvisos ?? "";
  const rapidas = e.respuestasRapidas ?? [];
  const lleno = rapidas.length >= MAX_RESPUESTAS_RAPIDAS;

  const setRapidas = (r: RespuestaRapida[]) => f.set("respuestasRapidas", r);
  const cambiarRapida = (i: number, campo: "titulo" | "texto", valor: string) =>
    setRapidas(rapidas.map((r, j) => (j === i ? { ...r, [campo]: valor } : r)));
  const agregar = () => {
    if (lleno) return;
    setRapidas([...rapidas, { id: idRespuestaRapida(rapidas.map((r) => r.id)), titulo: "", texto: "" }]);
  };
  const quitar = (i: number) => setRapidas(rapidas.filter((_, j) => j !== i));

  const guardar = async () => {
    const mailLimpio = mail.trim();
    const rapidasLimpias = limpiarRespuestasRapidas(rapidas);
    const sucio = mailLimpio !== mail || rapidasLimpias.some((r, i) => r.titulo !== rapidas[i].titulo || r.texto !== rapidas[i].texto);
    if (!sucio) { await f.guardar(); return; }
    // Sólo se tocan las claves que vinieron: una config vieja sin `mailAvisos` no tiene por qué ganar un "".
    if (mailLimpio !== mail) f.set("mailAvisos", mailLimpio);
    if (e.respuestasRapidas !== undefined) f.set("respuestasRapidas", rapidasLimpias);
    setGuardarLimpio(true);
  };

  return (
    <div className="space-y-5">
      <Tarjeta titulo="Aviso por mail" right={<Mail size={16} className="text-slate-400" />}>
        <div className="space-y-3">
          <div>
            <label htmlFor={idMail} className="text-sm font-medium text-slate-700">Mail que recibe el aviso</label>
            <input id={idMail} type="text" inputMode="email" autoComplete="email" value={mail} placeholder="encargado@marca.com"
              onChange={(ev) => f.set("mailAvisos", ev.target.value)} aria-invalid={!!f.errores.mailAvisos || undefined}
              className={`mt-1 block w-full max-w-md rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${f.errores.mailAvisos ? "border-red-300" : "border-slate-200"}`} />
            {f.errores.mailAvisos && <p className="text-xs text-red-600 mt-1">{f.errores.mailAvisos}</p>}
          </div>
          <p className="text-xs text-slate-500">
            Si alguien escribe y nadie le contesta en 15 minutos, te mandamos un mail (como mucho uno cada 2 horas). El mail no
            incluye lo que escribió la persona ni su número. Dejalo vacío para no recibir avisos.
          </p>
          {/* Sólo con `false` literal: un backend anterior no manda el dato, y avisar sin saber enseña a ignorarlo. */}
          {cuenta?.smtpConfigurado === false && (
            <AvisoAmbar>El servidor todavía no tiene configurado el envío de mails (SMTP): el aviso no va a salir hasta que lo configuren.</AvisoAmbar>
          )}
        </div>
      </Tarjeta>

      <Tarjeta titulo="Respuestas rápidas" right={<MessageSquareText size={16} className="text-slate-400" />}>
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Hasta 5 textos que se insertan en la caja de respuesta con un clic (por ejemplo: horario y dirección, delivery, cómo
            reservar). Se pueden editar antes de mandar.
          </p>
          {rapidas.length > 0 && (
            <ul className="space-y-3">
              {rapidas.map((r, i) => (
                <FilaRapida key={r.id || i} r={r} onCambiar={(campo, v) => cambiarRapida(i, campo, v)} onQuitar={() => quitar(i)} />
              ))}
            </ul>
          )}
          {f.errores.respuestasRapidas && <p className="text-xs text-red-600">{f.errores.respuestasRapidas}</p>}
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={agregar} disabled={lleno}
              className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
              <Plus size={15} /> Agregar respuesta rápida
            </button>
            {lleno && <span className="text-xs text-slate-500">Ya hay 5: es el máximo.</span>}
          </div>
        </div>
      </Tarjeta>

      <BotoneraGuardar f={{ ...f, guardar }} />
    </div>
  );
}

function FilaRapida({ r, onCambiar, onQuitar }: {
  r: RespuestaRapida; onCambiar: (campo: "titulo" | "texto", valor: string) => void; onQuitar: () => void;
}) {
  const idTitulo = useId();
  const idTexto = useId();
  return (
    <li className="rounded-xl border border-slate-200 p-3 space-y-2">
      <div className="flex items-end gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline justify-between gap-2">
            <label htmlFor={idTitulo} className="text-sm font-medium text-slate-700">Título</label>
            <span className="text-[11px] tabular-nums text-slate-400">{r.titulo.length}/{MAX_TITULO_RAPIDA}</span>
          </div>
          <input id={idTitulo} value={r.titulo} maxLength={MAX_TITULO_RAPIDA} onChange={(ev) => onCambiar("titulo", ev.target.value)}
            placeholder="Horario y dirección"
            className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </div>
        <button type="button" onClick={onQuitar}
          className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 hover:text-red-700">
          <Trash2 size={14} /> Quitar
        </button>
      </div>
      <div>
        <div className="flex items-baseline justify-between gap-2">
          <label htmlFor={idTexto} className="text-sm font-medium text-slate-700">Texto</label>
          <span className="text-[11px] tabular-nums text-slate-400">{r.texto.length}/{MAX_TEXTO_RAPIDA}</span>
        </div>
        <textarea id={idTexto} rows={3} value={r.texto} maxLength={MAX_TEXTO_RAPIDA} onChange={(ev) => onCambiar("texto", ev.target.value)}
          className="mt-1 block w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
      </div>
    </li>
  );
}
